/**
 * 017: structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/category-list/columns.tsx
 *
 * Only the data, columns and labels are ours. Reuses the same source brand's own columns.tsx cites
 * (specs/017-isomorphic-theme-only/contracts/screen-map.md row 10).
 */
import type { ColumnDef } from "@tanstack/react-table";
import { AvatarCard } from "@steelyard/theme";
import { imageUrl } from "@steelyard/common";
import type { Product } from "../../../lib/services/catalog";
import { PiEyeDuotone as EyeIcon, PiPencilSimpleDuotone as PencilIcon } from "react-icons/pi";
import { RowActions } from "../../../components/table/row-actions";
import { StatusToggle } from "../../../components/table/status-toggle";
import { productPath } from "../../../lib/paths";

/**
 * `ProductView` carries `brandName`/`categoryName` beside the ids; the loader's `brandNameById` /
 * `categoryNameById` maps (which the filters need anyway) cover an older body without them. A name
 * neither knows renders as "—", never the id.
 */
export function productColumns(brandNameById: Map<string, string>, categoryNameById: Map<string, string>, setStatus?: (product: Product, active: boolean) => Promise<unknown>): ColumnDef<Product>[] {
  return [
    {
      id: "name",
      accessorKey: "name",
      header: "Name",
      size: 280,
      // The storefront gallery wins when a product has one; the packing image is the fallback, so
      // a product with only that still shows something instead of initials. Both are
      // content-addressed hashes now, not URLs — either one renders through `imageUrl`.
      cell: ({ row }) => {
        const { images, primaryImageHash, name } = row.original;
        const hash = images[0] ?? primaryImageHash;
        return <AvatarCard src={hash ? imageUrl(hash) : ""} name={name} />;
      },
    },
    {
      id: "brand",
      // An accessor so the search box matches on the brand name too, not only the product's.
      accessorFn: (product) => product.brandName ?? brandNameById.get(product.brandId) ?? "—",
      header: "Brand",
      size: 160,
      cell: ({ getValue }) => getValue<string>(),
    },
    {
      id: "category",
      accessorFn: (product) => product.categoryName ?? categoryNameById.get(product.categoryId) ?? "—",
      header: "Category",
      size: 160,
      cell: ({ getValue }) => getValue<string>(),
    },
    {
      id: "status",
      accessorKey: "isActive",
      header: "Status",
      size: 140,
      cell: ({ row }) => (
        <StatusToggle name={row.original.name} active={row.original.isActive} onChange={setStatus && ((next) => setStatus(row.original, next))} />
      ),
    },
    // No delete: only `deactivateProduct` (its own button on the detail page), same as brand and
    // category. The detail screen's own form is the edit surface, so the action is a pencil ("View" for a read-only role).
    {
      id: "actions",
      header: "",
      size: 90,
      cell: ({ row }) => (
        <RowActions
          name={row.original.name}
          primary={{ label: setStatus ? "Edit product" : "View product", icon: setStatus ? PencilIcon : EyeIcon, to: productPath(row.original.slug) }}
        />
      ),
    },
  ];
}
